import { Component, OnDestroy, OnInit, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { SidebarService } from 'src/app/services/shared/sidebar.service';
import { SessionService } from 'src/app/services/shared/session.service';
import { ThemeService } from 'src/app/services/shared/theme.service';
import { CreditsModalService } from 'src/app/services/shared/credits-modal.service';
import { ReleaseTimelineModalService } from 'src/app/services/shared/release-timeline-modal.service';
import { IAccount } from 'src/app/models/IAccount.model';
import { APP_VERSION, APP_BRANCH, APP_COMMIT, APP_BUILT_AT } from 'src/environments/version';
import { environment } from 'src/environments/environment';
import { Helpers } from 'src/app/models/helpers';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})


export class SidebarComponent implements OnInit, OnDestroy {

  helpers = Helpers;
  public account: IAccount;
  private renderer!: Renderer2;
  // Build-time identity for the current build. The three values come from
  // scripts/generate-version.js (run on postinstall, prestart, prebuild).
  //
  // Display rules (rendered in sidebar.component.html):
  //  - semver: ALWAYS visible (large)
  //  - branch + short SHA: visible ONLY when NOT in production build
  //    (i.e. local `ng serve` or non-prod configurations). In prod they
  //    are still in the JS bundle but the template hides them.
  //
  // The branch is truncated to keep the sidebar layout stable for long
  // CI/feature branch names (e.g. `feature/phase-0-stabilization`).
  public desktopVersion: string = APP_VERSION;
  public desktopBranch: string = SidebarComponent.truncate(APP_BRANCH, 24);
  public desktopCommit: string = APP_COMMIT;
  /**
   * Data di build formattata "gg MMM yyyy" (es. "8 Lug 2026"). Renderizzata
   * sempre in sidebar (anche in prod) — richiesta 2026-07-08 per tracciare
   * quale build sta girando su ogni ambiente. Il timestamp completo
   * (ISO con ora) resta disponibile in `APP_BUILT_AT` per debug.
   */
  public desktopBuildDate: string = SidebarComponent.formatBuildDate(APP_BUILT_AT);
  public showDebugVersion: boolean = !environment.production;

  /** Truncate to `n` chars, replacing the tail with `…` if longer. */
  private static truncate(value: string, n: number): string {
    return value.length <= n ? value : `${value.slice(0, n - 1)}…`;
  }

  /**
   * Format ISO timestamp → "gg MMM yyyy" locale italiano.
   * Fallback safe: se il timestamp è invalido (build senza git / degraded)
   * mostra "—" invece di crashare.
   */
  private static formatBuildDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
                    'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }

  // Dark theme toggle — re-abilitato in v2.14.0 (Sprint 2 Track A · Fase 4)
  // dopo il refactor design-token: i restyle Phase 1 ora usano
  // `var(--token)` che si aggiornano automaticamente al flip di
  // `body.dark`. Lo state qui è derivato via getter dal `ThemeService`
  // (source of truth), così ogni cambio di tema riflette istantaneo
  // sulla UI del toggle senza dover subscribed/unsubscribed manuale.
  //
  // Storia:
  // - pre-v2.13.0: `isDark` field local + `body.classList.add('dark')`
  //   diretto in `toggleDarkMode()`. Propagation rotta (10/30 CSS var).
  // - v2.13.0 W3 (2026-07-01): toggle nascosto, dead code commentato.
  // - v2.14.0 (2026-07-02): re-add via `ThemeService` centralizzato +
  //   design token system che copre tutti i restyle Phase 1.
  get isDark(): boolean {
    return this.themeService.currentTheme === 'dark';
  }

  get theme(): string {
    return this.isDark ? 'Tema scuro' : 'Tema chiaro';
  }

  dataItem: any;
  filteredSections: any[] = [];
  private routerSub!: Subscription;

  constructor(
    public rendererFactory: RendererFactory2,
    public sidebarService: SidebarService,
    public authServ: AuthService,
    private session: SessionService,
    private router: Router,
    private themeService: ThemeService,
    private creditsModal: CreditsModalService,
    private timelineModal: ReleaseTimelineModalService,
  ) {
    this.renderer = rendererFactory!.createRenderer(null, null);
  }

  /**
   * Handler click sul logo Diakonix in header sidebar.
   * Apre il modale "Credits" con l'elenco del team di sviluppo.
   *
   * Storia:
   *  - v2.14.1: apriva un easter egg self-destruct.
   *  - v2.14.2: sostituito con feature pubblica (Credits) più utile
   *    per tutti i colleghi. L'easter egg del maintainer è ora
   *    keyboard-only (secretWord globale in `secrets.ts`).
   */
  onLogoClick(): void {
    this.creditsModal.open();
  }

  /**
   * Handler click sulla versione (v{{ desktopVersion }}) in sidebar.
   * Apre il modale "Roadmap Diakonix" con timeline delle release.
   * Utile per team dev + PM + cliente per capire l'evoluzione del
   * prodotto e cosa c'è di nuovo in ogni versione.
   */
  onVersionClick(): void {
    this.timelineModal.open();
  }

  ngOnInit(): void {
    this.getAccounts();
    console.log('Account is', this.account);
    const role = this.authServ.permissionUsed.value;
    this.filteredSections = this.menuSections.filter(
      section => !section.roles || section.roles.includes(role)
    );
    this.routerSub = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => this.openActiveSection(e.urlAfterRedirects));
    this.openActiveSection(this.router.url);
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  private openActiveSection(url: string): void {
    const segment = url.split('/').filter(s => s).pop() ?? '';
    const active = this.menuSections.find(section =>
      section.items.some(item => item.route && segment.startsWith(item.route))
    );
    if (active) {
      this.menuSections.forEach(s => s.open = false);
      active.open = true;
    }
  }

  toggleSidenav() {
    this.sidebarService.isClosed = !this.sidebarService.isClosed;
    this.sidebarService.toggleSidebar();
  }

  /**
   * Delegato al `ThemeService` centralizzato (v2.14.0). Il service gestisce:
   *   - flip light/dark
   *   - persistenza in localStorage['diakonix.theme']
   *   - broadcast reattivo a chiunque sia iscritto a `currentTheme$`.
   * La UI qui è update-free perché `isDark` / `theme` sono getter
   * che leggono da `themeService.currentTheme` — cambio automatico.
   */
  toggleDarkMode(): void {
    this.themeService.toggle();
  }

  getAccounts() {
    // TODO Fase 1: unificare i tipi CurrentUser (sessione) e IAccount
    // (response API) in un'unica interfaccia. Per ora cast via unknown.
    this.account = (this.session.getCurrentUser() ?? {}) as unknown as IAccount;
  }

  /**
   * Deep-link to the "Account Diakonix" page with the current user's
   * account view modal auto-opened.
   *
   * Storia:
   *  - pre-v2.14.2: apriva `<app-gestione-account-dettagli>` come
   *    overlay sopra la sidebar tramite `OpenModalService.onPhoto()`.
   *    Quel componente è stato rimosso in v2.3.0 (single-page restyle
   *    della gestione account) — il tag nel template era zombie che
   *    non renderizzava nulla ormai da anni.
   *  - v2.14.2: naviga a `/accounts?openAccountId=<id>`. La list
   *    component legge il query param al load e apre il view modal
   *    per l'account matchato. Match by `account_id` per evitare
   *    ambiguità: la sidebar mostra "Nome Cognome" mentre la lista
   *    "Cognome Nome" — il testo differisce ma l'id è univoco.
   *
   * No-op se non abbiamo un account_id (edge case: sessione non
   * ancora inizializzata quando l'utente clicca).
   */
  goToAccDetails(): void {
    const id = this.account?.account_id;
    if (id === undefined || id === null) return;
    this.router.navigate(['/accounts'], {
      queryParams: { openAccountId: id },
    });
  }

  menuSections = [
    {
      key: 'messages',
      label: 'Messaggi',
      icon: 'bxs-message-rounded-dots',
      open: false,
      colorClass: 'color-msg',
      roles: [Helpers.PermisionAdmin, Helpers.PermisionSuperAdmin],
      items: [
        { label: 'Messaggi', route: 'chat', icon: 'bx-message-rounded-dots', type: 'link' }
      ]
    },
    {
      key: 'wms',
      label: 'WMS',
      icon: 'bxs-cube',
      open: false,
      colorClass: 'color-wms',
      roles: [Helpers.PermisionAdmin, Helpers.PermisionSuperAdmin],
      items: [
        { label: 'Dashboard', route: 'dashboard', icon: 'bxs-dashboard', type: 'link' },
        { label: 'Assegnazione Ordini', route: 'assign-order', icon: 'bxs-box', type: 'link' },
        { label: 'Preparazione Ordini', route: 'prepare-order', icon: 'bxs-package', type: 'link' },
        { label: 'Ordini Parziali', route: 'partial-orders', icon: 'bxs-moon', type: 'link' },
        { label: 'Ordini PM', route: 'pm-orders', icon: 'bxs-star', type: 'link' },
        { label: 'Ordini da firmare', route: 'firmare', icon: 'bxs-edit', type: 'link' },
        { label: 'Inventario prodotti', route: 'products', icon: 'bxs-spreadsheet', type: 'link' }
      ]
    },
    {
      key: 'tracking',
      label: 'Tracking',
      icon: 'bxs-map-alt',
      open: false,
      colorClass: 'color-tracking',
      roles: [Helpers.PermisionAdmin, Helpers.PermisionSuperAdmin],
      items: [
        { label: 'Borderò', route: 'borderò', icon: 'bxs-file', type: 'link' },
        { label: 'Status Spedizioni', route: 'status', icon: 'bxs-map', type: 'link' },
        { label: 'Storico Ordini', route: 'history', icon: 'bxs-time', type: 'link' },
        { label: 'Storico Ordini parziali', route: 'storico-parziali', icon: 'bx-timer', type: 'link' }
      ]
    },
    {
      key: 'accounts',
      label: 'Accounts',
      icon: 'bxs-user',
      open: false,
      colorClass: 'color-accounts',
      items: [
        { label: 'Account Diakonix', route: 'accounts', icon: 'bxs-universal-access', type: 'link' },
        { label: 'Account agenti', route: 'agents', icon: 'bxs-user-account', type: 'link' },
        { label: 'Account fornitori', route: 'fornitore-accounts', icon: 'bxs-box', type: 'link' }
      ]
    },
    {
      key: 'quality',
      label: 'Controllo Qualità',
      icon: 'bxs-badge-check',
      open: false,
      colorClass: 'color-quality',
      roles: [Helpers.PermisionAdmin, Helpers.PermisionSuperAdmin],
      items: [
        { label: 'Controllo Qualità', route: 'quality', icon: 'bxs-badge-check', type: 'link' }
      ]
    },
    {
      key: 'suppliers',
      label: 'Fornitori',
      icon: 'bxs-building-house',
      open: false,
      colorClass: 'color-suppliers',
      roles: [Helpers.PermisionAdmin, Helpers.PermisionSuperAdmin],
      items: [
        { label: 'Tracciabilità', route: 'fornitori-tracciability', icon: 'bxs-map', type: 'link' },
        { label: 'Ordini a Fornitore', route: 'fornitori-ordini', icon: 'bxs-box', type: 'link' }
      ]
    },
    {
      key: 'statistics',
      label: 'Statistiche',
      icon: 'bxs-bar-chart-alt-2',
      open: false,
      colorClass: 'color-statistics',
      roles: [Helpers.PermisionAdmin, Helpers.PermisionSuperAdmin],
      items: [
        { label: 'Statistiche', route: 'stats', icon: 'bxs-bar-chart-alt-2', type: 'link' }
      ]
    },
    {
      key: 'settings',
      label: 'Impostazioni',
      icon: 'bxs-cog',
      open: false,
      colorClass: 'color-settings',
      roles: [Helpers.PermisionAdmin, Helpers.PermisionSuperAdmin],
      items: [
        { label: 'Impostazioni', route: 'settings', icon: 'bx-cog', type: 'link' },
        // Toggle tema — re-abilitato in v2.14.0 (Sprint 2 Track A F4)
        // dopo il refactor design-token. Wiring in `ThemeService`.
        { label: 'Tema', route: '', icon: 'bxs-palette', type: 'theme-toggle' }
      ]
    }
  ];

  toggle(section: any) {
    if (this.sidebarService.isClosed) return;
    this.menuSections.forEach(s => {
      if (s !== section) s.open = false;
    });
    section.open = !section.open;
  }

}
